import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TradingPanel = ({ selectedSymbol, currentPrice }) => {
  const [orderType, setOrderType] = useState('market');
  const [side, setSide] = useState('buy');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [leverage, setLeverage] = useState('1');

  const orderTypes = [
    { value: 'market', label: 'Market Order' },
    { value: 'limit', label: 'Limit Order' },
    { value: 'stop', label: 'Stop Order' },
    { value: 'stop_limit', label: 'Stop Limit' }
  ];

  const leverageOptions = [
    { value: '1', label: '1x' },
    { value: '2', label: '2x' },
    { value: '5', label: '5x' },
    { value: '10', label: '10x' },
    { value: '20', label: '20x' }
  ];

  const calculateOrderValue = () => {
    const qty = parseFloat(quantity) || 0;
    const orderPrice = orderType === 'market' ? currentPrice : (parseFloat(price) || currentPrice);
    return qty * orderPrice;
  };

  const calculatePnL = () => {
    const qty = parseFloat(quantity) || 0;
    const entryPrice = orderType === 'market' ? currentPrice : (parseFloat(price) || currentPrice);
    const slPrice = parseFloat(stopLoss) || 0;
    const tpPrice = parseFloat(takeProfit) || 0;
    
    const maxLoss = slPrice ? Math.abs((entryPrice - slPrice) * qty) : 0;
    const maxProfit = tpPrice ? Math.abs((tpPrice - entryPrice) * qty) : 0;
    
    return { maxLoss, maxProfit };
  };

  const handleSubmitOrder = () => {
    const orderData = {
      symbol: selectedSymbol,
      side,
      type: orderType,
      quantity: parseFloat(quantity),
      price: orderType !== 'market' ? parseFloat(price) : currentPrice,
      stopLoss: parseFloat(stopLoss) || null,
      takeProfit: parseFloat(takeProfit) || null,
      leverage: parseFloat(leverage),
      timestamp: new Date()
    };
    
    console.log('Order submitted:', orderData);
    // Reset form
    setQuantity('');
    setPrice('');
    setStopLoss('');
    setTakeProfit('');
  };

  const { maxLoss, maxProfit } = calculatePnL();

  return (
    <div className="bg-card border border-border rounded-lg p-4 h-full">
      {/* Panel Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Trading Panel</h3>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-success rounded-full" />
          <span className="text-sm text-muted-foreground">Connected</span>
        </div>
      </div>
      {/* Symbol Info */}
      <div className="bg-muted rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-foreground">{selectedSymbol}</div>
            <div className="text-xs text-muted-foreground">Current Price</div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold text-foreground">
              ${currentPrice?.toLocaleString()}
            </div>
            <div className="text-xs text-success">+2.45%</div>
          </div>
        </div>
      </div>
      {/* Buy/Sell Toggle */}
      <div className="flex bg-muted rounded-lg p-1 mb-4">
        <Button
          variant={side === 'buy' ? 'success' : 'ghost'}
          size="sm"
          onClick={() => setSide('buy')}
          className="flex-1"
        >
          <Icon name="TrendingUp" size={16} />
          Buy
        </Button>
        <Button
          variant={side === 'sell' ? 'destructive' : 'ghost'}
          size="sm"
          onClick={() => setSide('sell')}
          className="flex-1"
        >
          <Icon name="TrendingDown" size={16} />
          Sell
        </Button>
      </div>
      {/* Order Type */}
      <div className="mb-4">
        <Select
          label="Order Type"
          options={orderTypes}
          value={orderType}
          onChange={setOrderType}
        />
      </div>
      {/* Quantity Input */}
      <div className="mb-4">
        <Input
          label="Quantity"
          type="number"
          placeholder="0.00"
          value={quantity}
          onChange={(e) => setQuantity(e?.target?.value)}
          description={`Min: 0.001 ${selectedSymbol?.split('/')?.[0]}`}
        />
      </div>
      {/* Price Input (for limit orders) */}
      {orderType !== 'market' && (
        <div className="mb-4">
          <Input
            label="Price"
            type="number"
            placeholder="0.00"
            value={price}
            onChange={(e) => setPrice(e?.target?.value)}
            description="USD per unit"
          />
        </div>
      )}
      {/* Leverage */}
      <div className="mb-4">
        <Select
          label="Leverage"
          options={leverageOptions}
          value={leverage}
          onChange={setLeverage}
        />
      </div>
      {/* Risk Management */}
      <div className="space-y-4 mb-4">
        <Input
          label="Stop Loss"
          type="number"
          placeholder="0.00"
          value={stopLoss}
          onChange={(e) => setStopLoss(e?.target?.value)}
          description="Optional risk management"
        />
        <Input
          label="Take Profit"
          type="number"
          placeholder="0.00"
          value={takeProfit}
          onChange={(e) => setTakeProfit(e?.target?.value)}
          description="Optional profit target"
        />
      </div>
      {/* Order Summary */}
      {quantity && (
        <div className="bg-muted rounded-lg p-3 mb-4">
          <h4 className="text-sm font-medium text-foreground mb-2">Order Summary</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Order Value:</span>
              <span className="text-foreground font-medium">
                ${calculateOrderValue()?.toLocaleString()}
              </span>
            </div>
            {maxLoss > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Max Loss:</span>
                <span className="text-error font-medium">-${maxLoss?.toLocaleString()}</span>
              </div>
            )}
            {maxProfit > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Max Profit:</span>
                <span className="text-success font-medium">+${maxProfit?.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Fees (Est.):</span>
              <span className="text-foreground font-medium">
                ${(calculateOrderValue() * 0.001)?.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}
      {/* Submit Button */}
      <Button
        variant={side === 'buy' ? 'success' : 'destructive'}
        size="lg"
        fullWidth
        onClick={handleSubmitOrder}
        disabled={!quantity}
      >
        <Icon name={side === 'buy' ? 'TrendingUp' : 'TrendingDown'} size={16} />
        {side === 'buy' ? 'Place Buy Order' : 'Place Sell Order'}
      </Button>
      {/* Quick Actions */}
      <div className="flex items-center gap-2 mt-4">
        <Button variant="outline" size="sm" className="flex-1">
          <Icon name="Percent" size={14} />
          25%
        </Button>
        <Button variant="outline" size="sm" className="flex-1">
          <Icon name="Percent" size={14} />
          50%
        </Button>
        <Button variant="outline" size="sm" className="flex-1">
          <Icon name="Percent" size={14} />
          75%
        </Button>
        <Button variant="outline" size="sm" className="flex-1">
          <Icon name="Maximize" size={14} />
          Max
        </Button>
      </div>
    </div>
  );
};

export default TradingPanel;